import { LICENSE_VERIFICATION_REASSURANCE } from '@lighthouse/shared';

export type AuthVisualVariant = 'login' | 'register' | 'recovery' | 'onboarding' | 'admin';

interface Props {
  variant?: AuthVisualVariant;
  className?: string;
}

/** Original wellness artwork — organic shapes, journeys, connection motifs. */
export function AuthWellnessVisual({ variant = 'login', className = '' }: Props) {
  return (
    <div className={`auth-wellness-visual ${className}`} aria-hidden>
      <svg viewBox="0 0 400 420" className="auth-wellness-svg" fill="none">
        <defs>
          <linearGradient id="lh-glow" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,255,255,0.22)" />
            <stop offset="100%" stopColor="rgba(168,213,186,0.08)" />
          </linearGradient>
          <linearGradient id="lh-path" x1="0%" y1="100%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(212,168,120,0.5)" />
            <stop offset="50%" stopColor="rgba(255,255,255,0.35)" />
            <stop offset="100%" stopColor="rgba(90,158,120,0.45)" />
          </linearGradient>
        </defs>

        {/* Ambient organic blobs */}
        <ellipse cx="320" cy="72" rx="88" ry="72" fill="url(#lh-glow)" opacity="0.9" />
        <path
          d="M48 340c24-48 72-76 120-68 36 6 68 34 88 68-40 18-88 22-132 8-28-10-52-4-76-8z"
          fill="rgba(255,255,255,0.06)"
        />
        <path
          d="M280 300c20-32 56-48 92-40 28 6 52 26 64 52-32 14-68 16-100 4-20-8-38-10-56-16z"
          fill="rgba(168,213,186,0.12)"
        />

        {/* Journey path — register / onboarding */}
        {(variant === 'register' || variant === 'onboarding') && (
          <>
            <path
              d="M60 320 Q120 260 180 280 T300 220 T340 160"
              stroke="url(#lh-path)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="6 10"
              opacity="0.85"
            />
            <circle cx="60" cy="320" r="8" fill="rgba(212,168,120,0.7)" />
            <circle cx="180" cy="280" r="6" fill="rgba(255,255,255,0.5)" />
            <circle cx="300" cy="220" r="7" fill="rgba(168,213,186,0.65)" />
            <circle cx="340" cy="160" r="9" fill="rgba(255,255,255,0.75)" />
          </>
        )}

        {/* Balance / reflection — login */}
        {(variant === 'login' || variant === 'recovery' || variant === 'admin') && (
          <>
            <ellipse cx="200" cy="248" rx="72" ry="14" fill="rgba(255,255,255,0.08)" />
            <path
              d="M128 200c0-40 32-72 72-72s72 32 72 72"
              stroke="rgba(255,255,255,0.72)"
              strokeWidth="2"
            />
            <circle
              cx="200"
              cy="128"
              r="36"
              fill="rgba(255,255,255,0.16)"
              stroke="rgba(255,255,255,0.7)"
            />
            <circle cx="200" cy="128" r="18" fill="rgba(231,240,228,0.85)" />
          </>
        )}

        {/* Connection arcs — conversation & support */}
        <path
          d="M100 140c40-20 80-20 120 0"
          stroke="rgba(255,255,255,0.2)"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M180 120c30-16 60-16 90 0"
          stroke="rgba(212,168,120,0.35)"
          strokeWidth="2"
          strokeLinecap="round"
        />

        {/* Botanical leaf motifs */}
        <path
          d="M48 96c12-28 36-40 52-24s8 44-20 56c-16 8-36 4-32-32z"
          fill="rgba(90,158,120,0.22)"
        />
        <path
          d="M332 108c-10-24-32-34-46-20s-6 38 16 48c14 6 32 2 30-28z"
          fill="rgba(168,213,186,0.18)"
        />

        {/* Diverse silhouettes — abstract, not photographic */}
        {(variant === 'register' || variant === 'onboarding') && (
          <>
            <ellipse cx="148" cy="196" rx="22" ry="26" fill="rgba(255,255,255,0.14)" />
            <path
              d="M120 248c16 28 40 40 56 40s40-12 56-40"
              stroke="rgba(255,255,255,0.18)"
              strokeWidth="2"
              fill="none"
            />
            <ellipse cx="252" cy="188" rx="20" ry="24" fill="rgba(212,168,120,0.2)" />
            <path
              d="M228 236c14 24 36 34 48 34s34-10 48-34"
              stroke="rgba(255,255,255,0.15)"
              strokeWidth="2"
              fill="none"
            />
          </>
        )}

        {/* Bridge / resilience motif */}
        {variant === 'recovery' && (
          <path
            d="M80 300h240"
            stroke="rgba(255,255,255,0.2)"
            strokeWidth="2"
            strokeLinecap="round"
          />
        )}
      </svg>
    </div>
  );
}

export function AuthAmbientShapes({ className = '' }: { className?: string }) {
  return (
    <div className={`auth-ambient ${className}`} aria-hidden>
      <span className="auth-ambient-blob auth-ambient-blob-1" />
      <span className="auth-ambient-blob auth-ambient-blob-2" />
      <span className="auth-ambient-blob auth-ambient-blob-3" />
    </div>
  );
}

const ASIDE_COPY: Record<
  AuthVisualVariant,
  { headline: string; lines: string[]; reassurance?: string }
> = {
  login: {
    headline: 'A calm space to return to',
    lines: [
      'You are supported.',
      'You are in a trusted space.',
      'Professional help is here when you need it.',
    ],
    reassurance: 'Your sessions and data stay private and secure.',
  },
  register: {
    headline: 'Begin your journey with care',
    lines: [
      'Growth is possible.',
      'Compassionate, licensed professionals.',
      'A path designed around you.',
    ],
    reassurance: 'Join thousands taking their first step toward wellbeing.',
  },
  recovery: {
    headline: 'We’ll help you back in',
    lines: ['It happens — we’re here for you.', 'A few moments, then you’re back to your care.'],
    reassurance: 'Reset links are secure and expire quickly.',
  },
  onboarding: {
    headline: 'Share your gift of healing',
    lines: ['Help others find their way forward.', 'Verified, respected, and fairly compensated.'],
    reassurance: LICENSE_VERIFICATION_REASSURANCE,
  },
  admin: {
    headline: 'Stewarding safe care',
    lines: ['Protecting clients and therapists.', 'Operational clarity with compassion.'],
    reassurance: 'Admin access is audited and MFA-protected.',
  },
};

export function AuthAsideContent({ variant = 'login' }: { variant?: AuthVisualVariant }) {
  const copy = ASIDE_COPY[variant];
  return (
    <>
      <AuthWellnessVisual variant={variant} />
      <h2 className="auth-aside-headline">{copy.headline}</h2>
      <ul className="auth-aside-pillars">
        {copy.lines.map((line) => (
          <li key={line}>
            <span className="auth-aside-check" aria-hidden>
              ○
            </span>
            {line}
          </li>
        ))}
      </ul>
      {copy.reassurance && <p className="auth-aside-reassurance">{copy.reassurance}</p>}
    </>
  );
}
